import * as XLSX from "xlsx";

function slugify(text) {
  return String(text || "lista")
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);
}

function hasIssue(row, label) {
  return (row.system_issues || []).includes(label);
}

/** @type {Record<string, (row: object) => boolean>} */
const FILTERS = {
  missing_stock_codes: (p) => p.f5_reason === "code_not_in_financa5",
  missing_price_codes: (p) => p.f5_reason !== "skipped" && p.f5_reason !== "empty_product_code" && p.code && !p.f5_price_ok,
  missing_web_categories: (p) => hasIssue(p, "Pa kategori web"),
  missing_images: (p) => hasIssue(p, "Pa foto"),
  no_category_attributes: (p) => hasIssue(p, "Pa atribute në kategori") || hasIssue(p, "Pa kategori produkti"),
  unfilled_attributes: (p) => hasIssue(p, "Atribute të paplotësuara"),
  missing_code: (p) => hasIssue(p, "Pa kod"),
  missing_price: (p) => hasIssue(p, "Pa çmim në sistem"),
  composite_no_components: (p) => hasIssue(p, "Paketë bosh"),
  missing_barcode: (p) => hasIssue(p, "Pa barkod"),
  missing_category: (p) => hasIssue(p, "Pa kategori produkti"),
  missing_brand: (p) => hasIssue(p, "Pa markë"),
  missing_unit: (p) => hasIssue(p, "Pa njësi"),
  unfilled_required: (p) => (p.missing_required_attributes || []).length > 0,
  not_synced_erp: (p) => hasIssue(p, "Jo sinkronizuar ERP"),
  delisted_in_web: (p) => hasIssue(p, "Delistuar por në web"),
  inactive_in_web: (p) => hasIssue(p, "Jo aktiv por në web"),
  duplicate_code_products: (p) => !!p.is_duplicate_code,
};

/** @type {Record<string, string>} */
const FORMAT_BY_SECTION = {
  missing_web_categories: "web_categories",
  no_category_attributes: "attribute_issues",
  unfilled_attributes: "attribute_issues",
  unfilled_required: "attribute_issues",
  duplicate_code_products: "duplicate_codes",
};

function rowToProducts(row) {
  return {
    code: row.code ?? "",
    name: row.name ?? "",
    reason: row.reason ?? "",
    missing: row.missing_attributes || row.missing_required_attributes || row.missing || [],
    effective_web_cat_1: row.web_cat_1 ?? "",
    effective_web_cat_2: row.web_cat_2 ?? "",
    effective_web_cat_3: row.web_cat_3 ?? "",
    stored_web_cat_1: "",
    stored_web_cat_2: "",
    stored_web_cat_3: "",
    duplicate_count: row.duplicate_count ?? "",
    fis_status: row.fis_status ?? "",
    barcode: row.barcode ?? "",
    brand_name: row.brand_name ?? "",
    unit_name: row.unit_name ?? "",
  };
}

function buildRows(productsAudit, sectionKey, format) {
  const filter = FILTERS[sectionKey];
  if (!filter) return [];

  const rows = (productsAudit || []).filter(filter);

  if (format === "lines") {
    return rows.map((p) => ({ Kodi: p.code || "" }));
  }

  if (format === "products") {
    return rows.map((p) => ({
      Kodi: p.code || "",
      Emri: p.name || "",
      Barkod: p.barcode || "",
      Aktiv: p.status ? "Po" : "Jo",
      "Në web": p.in_web ? "Po" : "Jo",
      "ERP sync": p.fis_status ? "Po" : "Jo",
    }));
  }

  if (format === "attribute_issues") {
    return rows.map((p) => {
      const missing = p.missing_attributes || p.missing_required_attributes || p.missing || [];
      const detail = missing.length ? missing.join(", ") : p.reason || "";
      return { Kodi: p.code || "", Emri: p.name || "", Detaje: detail };
    });
  }

  if (format === "web_categories") {
    return rows.map((p) => ({
      Kodi: p.code || "",
      Emri: p.name || "",
      "Kategori web 1": p.web_cat_1 || "",
      "Kategori web 2": p.web_cat_2 || "",
      "Kategori web 3": p.web_cat_3 || "",
    }));
  }

  if (format === "duplicate_codes") {
    return rows.map((p) => ({
      Kodi: p.code || "",
      Emri: p.name || "",
    }));
  }

  return rows.map((p) => rowToProducts(p));
}

/**
 * @param {object[]} productsAudit
 * @param {string} sectionKey
 * @param {string} title
 * @param {string} [format]
 */
export function exportSyncHealthSection(productsAudit, sectionKey, title, format) {
  const resolvedFormat = format || FORMAT_BY_SECTION[sectionKey] || "products";
  const sheetRows = buildRows(productsAudit, sectionKey, resolvedFormat);

  if (!sheetRows.length) {
    throw new Error("Nuk ka të dhëna për eksport.");
  }

  const worksheet = XLSX.utils.json_to_sheet(sheetRows);
  const workbook = XLSX.utils.book_new();
  const sheetName = String(title || "Lista").slice(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const date = new Date().toISOString().split("T")[0];
  const filename = `${slugify(title)}_${date}.xlsx`;
  XLSX.writeFile(workbook, filename);
}

export function getSectionFilter(sectionKey) {
  return FILTERS[sectionKey] || null;
}
